"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Heart, Quote, Sparkles } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 0,
  quote: "VPNGENIX has provided my home network with military-grade security. I feel completely confident browsing, streaming, and working remotely every single day.",
  author: "Alex R.",
  position: "User",
  childInfo: "Verified Privacy Advocate",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_alex_r.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_alex_r.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_alex_r.png-wvc-sizes"
}, {
  id: 1,
  quote: "The connection speeds are incredible. My gaming and video conferencing have never been smoother or more secure.",
  author: "Jordan M.",
  position: "User",
  childInfo: "Professional Gamer",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_jordan_m.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_jordan_m.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_jordan_m.webp-wvc-sizes",
  stickerColor: "bg-chart-4",
  rotation: "rotate-2"
}, {
  id: 2,
  quote: "Warm, professional, and truly invested in digital sovereignty. We feel like part of a secure global community here.",
  author: "Sam K.",
  position: "User",
  childInfo: "Remote Consultant",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_sam_k.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_sam_k.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_sam_k.png-wvc-sizes",
  stickerColor: "bg-secondary",
  rotation: "-rotate-2"
}, {
  id: 3,
  quote: "The intuitive interface and robust encryption make all the difference. Our data stays protected across all devices!",
  author: "Priya V.",
  position: "User",
  childInfo: "Tech Entrepreneur",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_priya_v.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_priya_v.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_priya_v.webp-wvc-sizes"
}, {
  id: 4,
  quote: "Communication with the support team is wonderful. We always feel informed and connected to the latest security updates.",
  author: "Daniel L.",
  position: "User",
  childInfo: "Cybersecurity Analyst",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_daniel_l.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_daniel_l.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_daniel_l.webp-wvc-sizes",
  stickerColor: "bg-chart-4",
  rotation: "rotate-2"
}, {
  id: 5,
  quote: "A powerful tool for digital freedom. The global server network has brought out the best in my remote work capabilities.",
  author: "Elena T.",
  position: "User",
  childInfo: "Digital Nomad",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_elena_t.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_elena_t.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_elena_t.png-wvc-sizes",
  stickerColor: "bg-secondary",
  rotation: "-rotate-2"
}];
const accreditations = ["AES-256 Encryption", "No-Logs Policy Verified", "Global Server Network", "24/7 Expert Support"];
export default function Reviews() {
  const [activeGroup, setActiveGroup] = useState(0);
  const totalGroups = Math.ceil(testimonials.length / 3);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const startIdx = activeGroup * 3;
  const featured = testimonials[startIdx];
  const side1 = testimonials[startIdx + 1] ?? testimonials[0];
  const side2 = testimonials[startIdx + 2] ?? testimonials[1];
  return <section data-section-id="328" id="reviews" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-lg bg-accent/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-80 h-80 rounded-lg bg-chart-4/30 blur-3xl" />

      <div ref={ref} className={`relative mx-auto max-w-7xl px-5 sm:px-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.25em] uppercase text-muted-foreground font-default mb-5">
            <span>User Testimonials</span>
            <Sparkles className="w-3.5 h-3.5 text-secondary" strokeWidth={1.5} />
          </div>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-foreground tracking-tight">
            Voices of our{" "}
            <span className="relative inline-block text-primary">
              community
              <Heart className="absolute -top-3 -right-7 w-6 h-6 text-primary fill-primary/70 rotate-12" strokeWidth={1.5} />
            </span>
            .
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Real feedback from the users who trust VPNGENIX with their digital privacy and security.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 items-start">
          <article className="lg:col-span-3 relative bg-card text-card-foreground rounded-lg p-8 sm:p-10 md:p-12 shadow-lg border border-border">
            <div className="absolute -top-5 -left-3 sm:-left-5 w-14 h-14 rounded-lg bg-primary shadow-md rotate-[-8deg] flex items-center justify-center">
              <Quote className="w-6 h-6 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <Sparkles className="absolute top-6 right-6 w-5 h-5 text-secondary" strokeWidth={1.5} />

            <blockquote className="font-default italic text-foreground text-xl sm:text-2xl md:text-[1.6rem] leading-[1.45] tracking-tight">
              &ldquo;{featured.quote}&rdquo;
            </blockquote>

            <div className="mt-8 flex items-center gap-4 pt-6 border-t border-border">
              <SafeHydrate><Avatar className="size-14 rounded-lg ring-2 ring-background shadow-md">
                <AvatarImage src={featured.image} data-wvc-srcset={featured.srcset} data-wvc-sizes={featured.sizes} alt={featured.author} />
                <AvatarFallback className="rounded-lg bg-accent text-accent-foreground font-default font-semibold">
                  {featured.author.charAt(0)}
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <div>
                <div className="font-default font-semibold text-foreground text-base">
                  {featured.author}
                </div>
                <div className="text-sm text-muted-foreground font-default">
                  {featured.childInfo}
                </div>
              </div>
            </div>
          </article>

          <div className="lg:col-span-2 flex flex-col gap-6 md:gap-8">
            {[side1, side2].map((t, i) => <article data-index={i} key={`${activeGroup}-${t.id}`} className={`relative bg-card text-card-foreground rounded-lg p-6 sm:p-7 shadow-md border border-border transition-transform duration-300 hover:rotate-0`}>
                <div className={`absolute -top-3 -right-3 w-10 h-10 rounded-lg shadow-md flex items-center justify-center ${i === 0 ? "bg-primary" : "bg-secondary"}`}>
                  <Quote className={`w-4 h-4 text-white`} strokeWidth={2} fill="currentColor" />
                </div>

                <blockquote className="font-default italic text-foreground text-base sm:text-lg leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <div className="mt-5 flex items-center gap-3 pt-4 border-t border-border">
                  <SafeHydrate><Avatar className="size-10 rounded-lg ring-2 ring-background shadow-sm">
                    <AvatarImage src={t.image} data-wvc-srcset={t.srcset} data-wvc-sizes={t.sizes} alt={t.author} />
                    <AvatarFallback className="rounded-lg bg-accent text-accent-foreground text-sm font-default font-semibold">
                      {t.author.charAt(0)}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div>
                    <div className="font-default font-semibold text-foreground text-sm">
                      {t.author}
                    </div>
                    <div className="text-xs text-muted-foreground font-default">
                      {t.childInfo}
                    </div>
                  </div>
                </div>
              </article>)}
          </div>
        </div>

        <div className="mt-12 md:mt-16 flex items-center justify-center gap-3">
          {Array.from({
          length: totalGroups
        }).map((_, i) => <Button data-index={i} key={i} onClick={() => setActiveGroup(i)} variant="ghost" size="icon" aria-label={`Show testimonial set ${i + 1}`} className={`h-2.5 p-0 rounded-lg transition-all duration-300 hover:bg-transparent ${i === activeGroup ? "w-10 bg-primary hover:bg-primary" : "w-2.5 bg-border hover:bg-muted-foreground/40"}`} />)}
        </div>

        <div className="mt-16 md:mt-20 pt-10 border-t border-border">
          <p className="text-center text-xs tracking-[0.25em] uppercase text-muted-foreground font-default mb-6">
            Trusted &amp; Secure
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 items-center">
            {accreditations.map((item, i) => <div data-index={i} key={item} className="flex items-center justify-center gap-2 text-primary">
                <Sparkles className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm font-default font-medium tracking-wide text-center">
                  {item}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}